/* ============================================================================
   GRC Dashboard - configurable card catalog (metadata only)

   The live data + JSX for each card is built inside GRCManagerDashboard (it
   needs component state). This file just describes the cards so the "Add cards"
   panel and the grid know each card's title, icon, category and footprint.
   ========================================================================== */

import {
  Building2, Gauge, AlertTriangle, FileWarning, ClipboardList,
  BarChart2, PieChart, Table2, FileCheck2,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

/* Card footprint on the 5-column grid (preserves the original layout):
   sm = 1 col (KPI), md = 2 cols, wide = 3 cols, full = whole row. */
export type GrcCardSize = "sm" | "md" | "wide" | "full";

export interface GrcCardMeta {
  id: string;
  title: string;
  description: string;
  category: string;
  size: GrcCardSize;
  icon: LucideIcon;
  about?: string; // longer "what & why" shown on the card preview's flip side
}

export const GRC_CATEGORIES = ["KPIs", "Charts", "Tables"] as const;

export const GRC_CARDS: GrcCardMeta[] = [
  /* -- KPIs -- */
  { id: "total_orgs",          title: "Total Organizations",   description: "Organizations in the group",        category: "KPIs",   size: "sm",   icon: Building2,     about: "Counts every organization in your group. It gives you the scope of the portfolio at a glance  how many entities you're governing in one number." },
  { id: "active_ssps",         title: "Active SSPs",            description: "Active / published SSPs",            category: "KPIs",   size: "sm",   icon: FileCheck2,    about: "The number of published / active System Security Plans across the group, showing how much of the portfolio has an approved security plan in place." },
  { id: "group_compliance",    title: "Group Compliance Score", description: "Overall group compliance %",        category: "KPIs",   size: "sm",   icon: Gauge,        about: "The overall compliance score for the selected scope, rolled up from control implementation results, expressed as a single percentage to gauge overall health." },
  { id: "high_risk_orgs",      title: "High Risk Applications", description: "Apps flagged high risk",            category: "KPIs",   size: "sm",   icon: AlertTriangle, about: "How many applications are currently flagged as high risk. It helps you prioritise where attention and remediation are needed first." },
  { id: "open_findings",       title: "Open Findings",          description: "Total unresolved findings",         category: "KPIs",   size: "sm",   icon: FileWarning,  about: "The total number of unresolved POA&M findings (open issues awaiting remediation), highlighting outstanding compliance gaps that still need attention." },
  { id: "active_assessments",  title: "Approved Assessments",   description: "Assessments in progress",            category: "KPIs",   size: "sm",   icon: ClipboardList, about: "A headline counter of assessments currently in progress (approved and actively being executed), giving a quick pulse on ongoing assessment activity." },

  /* -- Charts -- */
  { id: "assessment_status",      title: "Assessment Status Overview", description: "Active assessments by stage", category: "Charts", size: "wide", icon: BarChart2, about: "Shows all assessment plans grouped by their stage in the lifecycle (Draft, Approved, In Progress, Completed, and Overdue) so you can see where assessments are bottlenecked across the selected scope." },
  { id: "top_risk_contributors",  title: "Top Risk Contributors",      description: "Share of total open findings", category: "Charts", size: "md",   icon: PieChart,  about: "A ranked list of the organizations generating the most open findings, showing which org units contribute the greatest share of risk across the group." },

  /* -- Tables -- */
  { id: "org_performance",     title: "Organization Performance", description: "Per-org metrics with drill-down", category: "Tables", size: "full", icon: Table2, about: "A per-organization rollup of compliance scores and risk metrics, letting you compare how each org unit under the group is performing side by side." },
];

export const GRC_CARD_MAP: Record<string, GrcCardMeta> =
  Object.fromEntries(GRC_CARDS.map((c) => [c.id, c]));

/* Default cards + order on a fresh dashboard. */
export const GRC_DEFAULT_LAYOUT: string[] = GRC_CARDS.map((c) => c.id);

/* localStorage key for this dashboard's saved layout (mock backend). */
export const GRC_LAYOUT_KEY = "rego.grcDashboardLayout.v1";

/* Tailwind grid span per size (base grid is 5 cols on lg). */
export const GRC_SPAN_CLASS: Record<GrcCardSize, string> = {
  sm:   "col-span-1",
  md:   "sm:col-span-2 lg:col-span-2",
  wide: "sm:col-span-2 lg:col-span-3",
  full: "sm:col-span-2 lg:col-span-5",
};
